// Button (drawing v2.6, screen 2 "Every state, every control", row Button).
// Five looks: primary (the gradient, the ONE action a section asks for),
// navy, plain (the default), danger (red outline; `dangerFill` is the solid
// one a dialog's confirm uses) and quiet (a link-styled button). Open Sans in
// sentence case, 700 on primary and navy, 600 on the rest (decision 2).
//
// Loading: a spinner and the label's "-ing" form, the width kept, a second
// press ignored. Disabled: says why, in a tooltip, and who can help.

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { cx } from "./cx";

export type ButtonVariant = "primary" | "navy" | "plain" | "danger" | "dangerFill" | "quiet";
export type ButtonSize = "default" | "sm" | "tall";

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "disabled"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  iconRight?: ReactNode;
  /** The label while the action runs ("Saving"); the button keeps its width. */
  busy?: boolean;
  busyLabel?: ReactNode;
  /** Disabled, with the reason beside it in the tooltip (the standard: disabled always says why). */
  disabledReason?: string;
  /** Renders as a router link with the same look. */
  to?: string;
  /** Renders as a plain anchor (an external or download link). */
  href?: string;
  /** Hidden on a phone (the header folds Export and the like into More). */
  hideOnPhone?: boolean;
}

const VARIANT_CLASS: Record<ButtonVariant, string | false> = {
  primary: "pg-btn--pri",
  navy: "pg-btn--navy",
  plain: false,
  danger: "pg-btn--danger",
  dangerFill: "pg-btn--dangerfill",
  quiet: "pg-btn--ghost",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "plain", size = "default", icon, iconRight, busy, busyLabel, disabledReason, to, href, hideOnPhone, className, children, onClick, type, ...rest },
  ref,
) {
  const disabled = Boolean(disabledReason);
  const cls = cx(
    "pg-btn",
    VARIANT_CLASS[variant],
    size === "sm" && "pg-btn--sm",
    size === "tall" && "pg-btn--tall",
    busy && "is-busy",
    hideOnPhone && "pg-hide-p",
    className,
  );
  const label = typeof children === "string" || typeof children === "number" ? <span className="pg-btn__label">{children}</span> : children;
  const body = (
    <>
      {busy ? <span className="pg-spin" aria-hidden /> : icon}
      {busy && busyLabel ? <span className="pg-btn__label">{busyLabel}</span> : label}
      {!busy && iconRight}
    </>
  );
  if (to) {
    return (
      <Link className={cls} to={to} aria-disabled={disabled || undefined} title={disabledReason}>
        {body}
      </Link>
    );
  }
  if (href) {
    return (
      <a className={cls} href={href} aria-disabled={disabled || undefined} title={disabledReason}>
        {body}
      </a>
    );
  }
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      className={cls}
      aria-disabled={disabled || undefined}
      aria-busy={busy || undefined}
      title={disabledReason ?? rest.title}
      onClick={(e) => {
        if (disabled || busy) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      }}
      {...rest}
    >
      {body}
    </button>
  );
});
